import {
  getApiFilterKey,
  getGlobalPeriodColumnKeys,
} from "../pl-by-program/plTimeFilterUtils";
import { toReportApiFilters } from "../filterRelationshipUtils";

/** Display labels aligned to Power BI Monthly DFR */
const METRIC_LABEL_OVERRIDES = {
  totalDirectCost: "Direct Cost",
  directCost: "Direct Cost",
  totalCost: "Burdened Cost",
  burdenedCost: "Burdened Cost",
  grossMargin: "Gross Profit",
  grossProfit: "Gross Profit",
};

/** Normalize API / label casing variants → Power BI name */
function overrideMetricLabel(key, label) {
  if (METRIC_LABEL_OVERRIDES[key]) return METRIC_LABEL_OVERRIDES[key];
  if (!label) return label;
  const normalized = String(label).trim().toLowerCase();
  if (normalized === "total direct cost" || normalized === "total direct costs") {
    return "Direct Cost";
  }
  if (
    normalized === "total cost" ||
    normalized === "total costs" ||
    normalized === "burdened cost" ||
    normalized === "burdened costs"
  ) {
    return "Burdened Cost";
  }
  if (normalized === "gross margin" || normalized === "gross margins") {
    return "Gross Profit";
  }
  return label;
}

/** Map API metric rows to Power BI display labels. */
export function mapDfrMetricRows(rows) {
  if (!rows?.length) return rows || [];
  return rows.map((r) => ({
    ...r,
    label: overrideMetricLabel(r.key, r.label),
  }));
}

/** API filters for Monthly DFR — month/quarter are client-side only. */
export function buildDfrApiFilters(filters) {
  return toReportApiFilters(filters, [
    "year",
    "contractId",
    "projectId",
    "bu",
    "orgId",
    "contractType",
    "clinLvl2",
    "businessType",
    "awardType",
    "competitionType",
    "popEndDate",
    "sector",
    "contractName",
  ]);
}

export { getApiFilterKey, getGlobalPeriodColumnKeys };

/** Visible period keys including total, or null when showing full year. */
export function getVisibleDfrPeriods(filters, allPeriods) {
  const monthKeys = getGlobalPeriodColumnKeys(filters, "Month");
  if (!monthKeys?.length) return allPeriods;
  return [...monthKeys, "total"];
}

function sumPeriod(data, monthKeys) {
  return monthKeys.reduce((s, mk) => s + (Number(data?.[mk]) || 0), 0);
}

function avgPeriod(data, monthKeys) {
  const vals = monthKeys
    .map((mk) => Number(data?.[mk]))
    .filter((n) => Number.isFinite(n));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function weightedPct(measures, dollarKey, monthKeys) {
  const rev =
    measures?.contractRevenue || measures?.revenue || null;
  const dollars = measures?.[dollarKey] || null;
  if (!rev || !dollars) return null;
  const r = sumPeriod(rev, monthKeys);
  const d = sumPeriod(dollars, monthKeys);
  return r ? d / r : 0;
}

/**
 * Resolve a DFR measure cell. When month/quarter filters are active,
 * recomputes the Total column from the visible months (weighted for %).
 */
export function resolveDfrMeasureValue(
  measures,
  metricKey,
  period,
  isPct,
  periodMonthKeys,
) {
  const data = measures?.[metricKey];
  if (period !== "total" || !periodMonthKeys?.length) {
    return data?.[period];
  }

  if (!isPct) return sumPeriod(data, periodMonthKeys);

  if (metricKey === "grossMarginPct") {
    const weighted =
      weightedPct(measures, "grossProfit", periodMonthKeys) ??
      weightedPct(measures, "grossMargin", periodMonthKeys);
    if (weighted != null) return weighted;
  }
  if (metricKey === "netMarginPct") {
    const weighted =
      weightedPct(measures, "netProfit", periodMonthKeys) ??
      weightedPct(measures, "netProfitFee", periodMonthKeys);
    if (weighted != null) return weighted;
  }

  return avgPeriod(data, periodMonthKeys);
}
